import React, { useState, useEffect } from 'react';
import { People, Assignment, LocalHospital, CompareArrows, ManageAccountsOutlined } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import SwalWithA11yFix from '../../services/swalConfig';
import ClientManagement from '../../components/practitioner/ClientManagement';
import OrderManagement from '../../components/practitioner/OrderManagement';
import DirectReports from '../../components/practitioner/DirectReports';
import LabCatalogContent from '../../components/common/LabCatalogContent';
import CompareTestsModal from '../../components/common/CompareTestsModal';
import DashboardLayout from '../../components/common/DashboardLayout';
import ProfileEditor from '../../components/common/ProfileEditor';

const PractitionerDashboard = () => {
  // Default to Reports tab
  const [activeTab, setActiveTab] = useState(0);
  const [patients, setPatients] = useState([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [allTests, setAllTests] = useState([]);
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPatients();
    fetchAllTests();
  }, []);

  const fetchPatients = async () => {
    try {
      const response = await api.get('/practitioner/patients');
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchAllTests = async () => {
    try {
      const response = await api.get('/tests');
      setAllTests(response.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
    }
  };

  const handleLogout = async () => {
    const result = await SwalWithA11yFix({
      title: 'Are you sure?',
      text: 'You will be logged out of your account',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1976d2',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      logout();
      await SwalWithA11yFix({
        icon: 'success',
        title: 'Logged Out!',
        text: 'You have been successfully logged out.',
        timer: 1500,
        showConfirmButton: false,
      });
      navigate('/login');
    }
  };

  const menuItems = [
    {
      icon: <Assignment />,
      label: 'Reports',
      active: activeTab === 0,
      onClick: () => setActiveTab(0),
    },
    {
      icon: <Assignment />,
      label: 'Orders',
      active: activeTab === 1,
      onClick: () => setActiveTab(1),
    },
    {
      icon: <LocalHospital />,
      label: 'Lab Catalog',
      active: activeTab === 2,
      onClick: () => setActiveTab(2),
    },
    {
      icon: <People />,
      label: 'Clients',
      active: activeTab === 3,
      onClick: () => setActiveTab(3),
    },
    {
      icon: <ManageAccountsOutlined />,
      label: 'Edit Profile',
      active: activeTab === 4,
      onClick: () => setActiveTab(4),
    },
  ];

  // Get dynamic heading based on active tab
  const getTopHeading = () => {
    switch(activeTab) {
      case 0:
        return 'Reports';
      case 1:
        return `${user?.firstName}'s Orders`;
      case 2:
        return 'Lab Catalog';
      case 3:
        return ''; // Clients tab heading is shown in ClientManagement component
      case 4:
        return 'Edit Profile';
      default:
        return '';
    }
  };

  // Get CTA button based on active tab
  const getCtaButton = () => {
    switch(activeTab) {
      case 2: // Lab Catalog
        return (
          <Button
            variant="contained"
            startIcon={<CompareArrows />}
            onClick={() => setCompareModalOpen(true)}
            sx={{
              backgroundColor: '#1976d2',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              padding: '8px 16px',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            Compare Tests
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout
      title="Practitioner Dashboard"
      user={user}
      onLogout={handleLogout}
      menuItems={menuItems}
      logoutPath="/login"
      topHeading={getTopHeading()}
      ctaButton={getCtaButton()}
    >
      {activeTab === 0 && <DirectReports />}
      {activeTab === 1 && <OrderManagement patients={patients} onUpdate={fetchPatients} />}
      {activeTab === 2 && (
        <Box>
          <LabCatalogContent embedded={true} showComparison={true} />
          <CompareTestsModal
            open={compareModalOpen}
            onClose={() => setCompareModalOpen(false)}
            allTests={allTests}
            initialTest={null}
          />
        </Box>
      )}
      {activeTab === 3 && <ClientManagement patients={patients} onUpdate={fetchPatients} />}
      {activeTab === 4 && <ProfileEditor user={user} onProfileUpdated={updateUser} />}
    </DashboardLayout>
  );
};

export default PractitionerDashboard;

